import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../dataManagement/uiDb/UiDb";
import { createListingCollection } from "../../../services/thunks/ListingCollectionThunk";
import ConfirmModal from "../ConfirmModal/ConfirmModal";
import { useNotification } from "../contexts/NotificationContext";
import { NotificationType } from "../../../constants/Enums";
import "./styles/SaveAsCollectionModal.css";

/** Same cap as the admin Create Collection form's name field. */
export const COLLECTION_NAME_MAX_LENGTH = 80;

interface SaveAsCollectionModalProps {
  isOpen: boolean;
  /** Pre-filled name ("" for an empty field). */
  initialName?: string;
  productIds: string[];
  bannerUrl?: string | null;
  onClose: () => void;
}

/**
 * Asks for a name, then saves the given products as a listing collection owned
 * by the signed-in user -- through the same create API as the admin form.
 * Used by the cart "Collection" page and the public listing-collection page.
 */
const SaveAsCollectionModal: React.FC<SaveAsCollectionModalProps> = ({
  isOpen,
  initialName = "",
  productIds,
  bannerUrl,
  onClose,
}) => {
  const dispatch: AppDispatch = useDispatch();
  const { triggerNotification } = useNotification();
  const [name, setName] = useState(initialName);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen) setName(initialName);
  }, [isOpen, initialName]);

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed || isSaving || productIds.length === 0) return;
    setIsSaving(true);
    try {
      await dispatch(
        createListingCollection({
          collectionName: trimmed,
          productIds: Array.from(new Set(productIds)),
          bannerUrl: bannerUrl ?? undefined,
        })
      ).unwrap();
      triggerNotification("Saved to your collections.", NotificationType.SUCCESS);
      onClose();
    } catch (err) {
      // unwrap() rejects with a plain serialized error ({ message }), not an
      // Error instance, so ApiErrorHelper.rejectionMessage would drop the
      // backend's message here.
      const message = (err as { message?: string })?.message;
      triggerNotification(message || "Couldn't save this collection.", NotificationType.ERROR);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ConfirmModal
      isOpen={isOpen}
      title="Save as a collection"
      message={
        <input
          type="text"
          className="save-as-collection__input"
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSave();
          }}
          maxLength={COLLECTION_NAME_MAX_LENGTH}
          placeholder="Collection name"
          aria-label="Collection name"
        />
      }
      confirmLabel="Save"
      cancelLabel="Cancel"
      isLoading={isSaving}
      confirmDisabled={!name.trim()}
      onConfirm={handleSave}
      onCancel={() => {
        if (!isSaving) onClose();
      }}
    />
  );
};

export default SaveAsCollectionModal;
